/**
 * Landing mot de passe — intro au chargement + timeline maître (scrub easeSoft open/close).
 */
class PasswordLanding {
  #introTimeline = null;

  #masterTimeline = null;

  #scrubTween = null;

  #scrubDirection = 1;

  #isReady = false;

  #ease = () => window.GSAP_EASE_SOFT || 'power2.out';

  #motion() {
    if (this.#prefersReducedMotion()) {
      return {
        textDuration: 0.01,
        bgDuration: 0.01,
        stagger: 0,
        yShift: 0,
        introDelay: 0,
      };
    }

    return {
      textDuration: 0.72,
      bgDuration: 1.05,
      stagger: 0.09,
      yShift: 10,
      introDelay: 0.2,
    };
  }

  constructor(el) {
    this.root = el;
    if (!this.root) return;

    if (typeof gsap === 'undefined') return;

    this.bindUI();
    this.#clearFooterGsapProps();
    this.#setInitialState();
    this.bindEvents();
    this.#playIntro();
  }

  bindUI() {
    this.overlay = this.root.querySelector('.js-password-overlay');
    this.closeBtn = this.root.querySelector('.js-password-close');
    this.heroMain = this.root.querySelector('.js-password-hero-main');
    this.bgHero = this.root.querySelector('.js-password-bg-hero');
    this.bgHeroMobile = this.root.querySelector('.js-password-bg-hero-mobile');
    this.bgJoin = this.root.querySelector('.js-password-bg-join');
    this.bgJoinMobile = this.root.querySelector('.js-password-bg-join-mobile');
    this.heroTextLines = this.#getTextLines(this.root.querySelector('.js-password-hero-text'));
    this.heroCta = this.root.querySelector('.js-password-hero-cta');
    this.footer = this.root.querySelector('.js-password-footer');
    this.footerLogo = this.root.querySelector('.js-password-footer-logo');
    this.joinTitle = this.root.querySelector('.js-password-join-title');
    this.joinTextLines = this.#getTextLines(this.root.querySelector('.js-password-join-text'));
    this.joinWhatsappContent = this.root.querySelectorAll('.js-password-join-phone, .js-whatsapp-success');
    this.joinWhatsappActions = this.root.querySelector('.js-password-join-actions');
  }

  #getTextLines(container) {
    if (!container) return [];

    const lines = [...container.querySelectorAll('p, h1, h2, h3, h4, h5, h6')];
    return lines.length ? lines : [container];
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #setFooterInteractive(isInteractive) {
    if (!this.footer) return;

    this.footer.classList.toggle('pointer-events-none', !isInteractive);
  }

  #clearFooterGsapProps() {
    if (this.footer) {
      gsap.set(this.footer, { clearProps: 'all' });
    }

    if (this.footerLogo) {
      gsap.set(this.footerLogo, { clearProps: 'all' });
    }
  }

  #setInitialState() {
    const { yShift } = this.#motion();

    gsap.set(this.bgHero, { opacity: 0, scale: 1.04, transformOrigin: 'center center' });
    gsap.set(this.bgHeroMobile, { opacity: 0, scale: 1.04, transformOrigin: 'center center' });
    gsap.set(this.heroTextLines, { autoAlpha: 0, y: yShift });
    gsap.set(this.heroCta, { autoAlpha: 0, y: yShift * 0.85 });
    gsap.set(this.bgJoin, { opacity: 0 });
    gsap.set(this.bgJoinMobile, { opacity: 0 });
    gsap.set(this.joinTitle, { autoAlpha: 0, y: yShift });
    gsap.set(this.joinTextLines, { autoAlpha: 0, y: yShift });
    gsap.set(this.joinWhatsappContent, { autoAlpha: 0, y: yShift * 0.7 });
    gsap.set(this.joinWhatsappActions, { autoAlpha: 0, y: yShift * 0.7 });
    gsap.set(this.closeBtn, { autoAlpha: 0 });
    gsap.set(this.footer, { autoAlpha: 0, y: yShift * 0.85 });
    gsap.set(this.footerLogo, { autoAlpha: 0 });
    this.#setFooterInteractive(false);
  }

  #killScrubTween() {
    if (this.#scrubTween) {
      this.#scrubTween.kill();
      this.#scrubTween = null;
    }
  }

  #scrubMasterTimeline(targetProgress, onComplete) {
    const tl = this.#getMasterTimeline();
    const ease = this.#ease();
    const startProgress = tl.progress();
    const distance = Math.abs(targetProgress - startProgress);

    if (distance === 0) {
      onComplete?.();
      return;
    }

    this.#killScrubTween();
    this.#scrubDirection = targetProgress > startProgress ? 1 : -1;

    this.#scrubTween = gsap.to(tl, {
      progress: targetProgress,
      duration: tl.duration() * distance,
      ease,
      onComplete: () => {
        this.#scrubTween = null;
        tl.pause();

        if (targetProgress === 1) {
          this.#applyOpenState(true);
        }

        onComplete?.();
      },
    });
  }

  #isOpen() {
    return this.root.dataset.passwordOpen === 'true';
  }

  #setOverlayInteractive(isInteractive) {
    if (!this.overlay) return;

    if (isInteractive) {
      this.overlay.classList.remove('pointer-events-none');
      this.overlay.classList.add('pointer-events-auto');
      this.overlay.setAttribute('aria-hidden', 'false');
      this.closeBtn?.removeAttribute('tabindex');
      this.closeBtn?.classList.remove('pointer-events-none');
      this.closeBtn?.classList.add('pointer-events-auto');
    } else {
      this.overlay.classList.add('pointer-events-none');
      this.overlay.classList.remove('pointer-events-auto');
      this.overlay.setAttribute('aria-hidden', 'true');
      this.closeBtn?.setAttribute('tabindex', '-1');
      this.closeBtn?.classList.add('pointer-events-none');
      this.closeBtn?.classList.remove('pointer-events-auto');
    }
  }

  #isMobileView() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  #setMobileScrollLock(isLocked) {
    if (!this.#isMobileView()) return;

    this.root.classList.toggle('js-password-page--overlay-open', isLocked);

    if (isLocked) {
      this.overlay?.scrollTo(0, 0);
    }
  }

  #applyOpenState(isOpen) {
    this.root.dataset.passwordOpen = isOpen ? 'true' : 'false';
    this.#setOverlayInteractive(isOpen);
    this.#setMobileScrollLock(isOpen);
    this.heroMain?.classList.toggle('pointer-events-none', isOpen);
    this.#setFooterInteractive(!isOpen);
  }

  #prepareOpening() {
    this.root.dataset.passwordOpen = 'true';
    this.#setMobileScrollLock(true);
    this.heroMain?.classList.add('pointer-events-none');
    this.#setFooterInteractive(false);
  }

  #lockHeroClosedState() {
    const { yShift } = this.#motion();

    gsap.set(this.heroTextLines, { autoAlpha: 1, y: 0 });
    gsap.set(this.heroCta, { autoAlpha: 1, y: 0 });
    gsap.set(this.bgHero, { opacity: 1, scale: 1 });
    gsap.set(this.bgHeroMobile, { opacity: 1, scale: 1 });
    gsap.set(this.bgJoin, { opacity: 0 });
    gsap.set(this.bgJoinMobile, { opacity: 0 });
    gsap.set(this.joinTitle, { autoAlpha: 0, y: yShift });
    gsap.set(this.joinTextLines, { autoAlpha: 0, y: yShift });
    gsap.set(this.joinWhatsappContent, { autoAlpha: 0, y: yShift * 0.7 });
    gsap.set(this.joinWhatsappActions, { autoAlpha: 0, y: yShift * 0.7 });
    gsap.set(this.closeBtn, { autoAlpha: 0 });
    gsap.set(this.footer, { autoAlpha: 1, y: 0 });
    gsap.set(this.footerLogo, { autoAlpha: 1 });
  }

  #playIntro() {
    const ease = this.#ease();
    const { textDuration, bgDuration, stagger, introDelay } = this.#motion();

    this.#introTimeline = gsap.timeline({
      defaults: { ease, duration: textDuration },
      delay: introDelay,
      onComplete: () => {
        this.#isReady = true;
        this.root.dataset.passwordReady = 'true';
        this.#setFooterInteractive(true);
        this.#introTimeline = null;
      },
    });

    this.#introTimeline
      .to(this.bgHero, { opacity: 1, scale: 1, duration: bgDuration }, 0)
      .to(this.bgHeroMobile, { opacity: 1, scale: 1, duration: bgDuration }, 0)
      .to(this.heroTextLines, { autoAlpha: 1, y: 0, stagger, force3D: true }, 0.28)
      .to(this.heroCta, { autoAlpha: 1, y: 0, force3D: true }, '<0.18')
      .to(this.footerLogo, { autoAlpha: 1 }, '<0.12')
      .to(this.footer, { autoAlpha: 1, y: 0, force3D: true }, '<');
  }

  #getMasterTimeline() {
    if (this.#masterTimeline) return this.#masterTimeline;

    const { textDuration, bgDuration, stagger, yShift } = this.#motion();
    const yOut = -yShift;

    this.#masterTimeline = gsap.timeline({
      paused: true,
      defaults: { ease: 'none', duration: textDuration },
    })
      .addLabel('exitHero', 0)
      .fromTo(
        this.heroTextLines,
        { autoAlpha: 1, y: 0 },
        { autoAlpha: 0, y: yOut, stagger, immediateRender: false, force3D: true },
        'exitHero',
      )
      .fromTo(
        this.heroCta,
        { autoAlpha: 1, y: 0 },
        { autoAlpha: 0, y: yOut * 0.75, immediateRender: false, force3D: true },
        'exitHero+=0.08',
      )
      .fromTo(
        this.footer,
        { autoAlpha: 1, y: 0 },
        { autoAlpha: 0, y: yOut * 0.75, duration: textDuration * 0.65, immediateRender: false, force3D: true },
        'exitHero',
      )
      .fromTo(
        this.footerLogo,
        { autoAlpha: 1 },
        { autoAlpha: 0, duration: textDuration * 0.65, immediateRender: false },
        'exitHero',
      )
      .addLabel('crossfade', '>')
      .to(this.bgHero, { opacity: 0, duration: bgDuration }, 'crossfade')
      .to(this.bgHeroMobile, { opacity: 0, duration: bgDuration }, 'crossfade')
      .to(this.bgJoin, { opacity: 1, duration: bgDuration }, 'crossfade')
      .to(this.bgJoinMobile, { opacity: 1, duration: bgDuration }, 'crossfade')
      .addLabel('enterJoin', 'crossfade+=0.32')
      .fromTo(
        this.closeBtn,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.5, immediateRender: false },
        'enterJoin',
      )
      .fromTo(
        this.joinTitle,
        { autoAlpha: 0, y: yShift },
        { autoAlpha: 1, y: 0, immediateRender: false, force3D: true },
        'enterJoin',
      )
      .fromTo(
        this.joinTextLines,
        { autoAlpha: 0, y: yShift },
        { autoAlpha: 1, y: 0, stagger, immediateRender: false, force3D: true },
        'enterJoin+=0.08',
      )
      .fromTo(
        this.joinWhatsappContent,
        { autoAlpha: 0, y: yShift * 0.7 },
        { autoAlpha: 1, y: 0, duration: textDuration * 0.85, immediateRender: false, force3D: true },
        'enterJoin+=0.22',
      )
      .fromTo(
        this.joinWhatsappActions,
        { autoAlpha: 0, y: yShift * 0.7 },
        { autoAlpha: 1, y: 0, duration: textDuration * 0.85, immediateRender: false, force3D: true },
        'enterJoin+=0.34',
      )
      .add(() => {
        if (this.#scrubDirection < 0) {
          this.#setOverlayInteractive(false);
          return;
        }

        this.#applyOpenState(true);
      }, 'enterJoin');

    return this.#masterTimeline;
  }

  open() {
    if (!this.#isReady) return;

    const tl = this.#getMasterTimeline();
    if (tl.progress() === 1 && !this.#scrubTween) return;

    this.#prepareOpening();
    this.#scrubMasterTimeline(1);
  }

  closeOverlay() {
    this.close();
  }

  close() {
    const tl = this.#getMasterTimeline();
    if (tl.progress() === 0 && !this.#scrubTween) return;

    this.#scrubMasterTimeline(0, () => {
      this.#applyOpenState(false);
      this.#lockHeroClosedState();
      this.root.dispatchEvent(new CustomEvent('password:whatsapp-reset'));
    });
  }

  handleClick(event) {
    const openTrigger = event.target.closest('.js-password-open');
    if (openTrigger && this.root.contains(openTrigger)) {
      event.preventDefault();
      this.open();
      return;
    }

    const closeTrigger = event.target.closest('.js-password-close');
    if (closeTrigger && this.root.contains(closeTrigger)) {
      event.preventDefault();
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.#isOpen()) return;
    this.close();
  }

  bindEvents() {
    this.root.addEventListener('click', (event) => this.handleClick(event));
    this.root.addEventListener('password:close', () => this.close());
    document.addEventListener('keydown', (event) => this.handleKeydown(event));
  }
}

const initPasswordLanding = () => {
  document.querySelectorAll('.js-password-page').forEach((el) => {
    new PasswordLanding(el);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPasswordLanding);
} else {
  initPasswordLanding();
}
